import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';

/**
 * Formats a datetime ISO string to relative time ("3m ago", "5h ago", "2d ago").
 * Falls back to locale date string for dates > 30 days.
 */
function relativeTime(isoString) {
  if (!isoString) return '\u2014';
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

function fullDateTime(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString();
}

// ---------------------------------------------------------------------------
// Column definitions — Cloud campus (9 columns)
// ---------------------------------------------------------------------------
export const SERVER_CLOUD_COLUMN_DEFS = [
  { key: 'is_offline', label: 'Online Status', sortable: true, filterable: true },
  { key: 'description', label: 'Description', sortable: true, filterable: true },
  { key: 'palcare_interface_name', label: 'Service', sortable: true, filterable: true },
  { key: 'software_version', label: 'Version', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'ip_address', label: 'IP Address', sortable: true, filterable: true },
  { key: 'mac_addr', label: 'Mac Address', sortable: true, filterable: true },
  { key: 'is_active', label: 'Implementation', sortable: true, filterable: true },
  { key: 'last_heard', label: 'Last Update', sortable: true, filterable: false },
];

// ---------------------------------------------------------------------------
// Column definitions — Non-cloud campus (6 columns)
// ---------------------------------------------------------------------------
export const SERVER_COLUMN_DEFS = [
  { key: 'description', label: 'Description', sortable: true, filterable: true },
  { key: 'palcare_interface_name', label: 'Service', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'ip_address', label: 'IP Address', sortable: true, filterable: true },
  { key: 'mac_addr', label: 'Mac Address', sortable: true, filterable: true },
  { key: 'is_active', label: 'Status', sortable: true, filterable: true },
];

/**
 * ServersTable — full data table for PalCare server devices with
 * sorting, filtering, sticky header, row selection, and row actions.
 */
const ServersTable = ({
  data,
  allData,
  visibleColumns,
  columnDefs,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onDeleteDevice,
  deleteRights,
}) => {
  const activeColumns = columnDefs.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      if (columnKey === 'is_active') {
        values.add(val ? 'Active' : 'Backup');
      } else if (columnKey === 'is_offline') {
        values.add(val ? 'Offline' : 'Online');
      } else {
        values.add(val != null && val !== '' ? String(val) : '(empty)');
      }
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const showCheckbox = deleteRights;
  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.palcare_device_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'description':
        return row.description && row.palcare_device_id ? (
          <Link
            to={'/community/server_devices/' + row.palcare_device_id}
            className="dp-device-link"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.description}
          </Link>
        ) : (
          <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.description || '\u2014'}
          </span>
        );

      case 'area_name':
        return row.area_id ? (
          <Link to={'/community/areas/' + row.area_id} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        ) : (
          <span className="dp-cell-text">{row.area_name || '—'}</span>
        );

      case 'mac_addr':
        return row.mac_addr && row.palcare_device_id ? (
          <Link
            to={'/community/server_devices/' + row.palcare_device_id}
            className="dp-device-link"
          >
            {row.mac_addr}
          </Link>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'is_active':
        return row.is_active ? (
          <span className="dp-status dp-status--active">
            <span className="dp-status__dot" aria-hidden="true" />
            Active
          </span>
        ) : (
          <span className="dp-status dp-status--inactive">
            <span className="dp-status__dot" aria-hidden="true" />
            Backup
          </span>
        );

      case 'is_offline':
        // Inverted: false = Online, true = Offline
        return row.is_offline ? (
          <span className="dp-status dp-status--warning">
            <span className="dp-status__dot" aria-hidden="true" />
            Offline
          </span>
        ) : (
          <span className="dp-status dp-status--active">
            <span className="dp-status__dot" aria-hidden="true" />
            Online
          </span>
        );

      case 'last_heard':
        return row.last_heard ? (
          <span className="dp-cell-text dp-cell-text--secondary" title={fullDateTime(row.last_heard)}>
            {relativeTime(row.last_heard)}
          </span>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            {showCheckbox ? (
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all servers"
                />
              </th>
            ) : null}

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th">
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">
              <span className="dp-sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.palcare_device_id);
            return (
              <tr
                key={row.palcare_device_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                {showCheckbox ? (
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.palcare_device_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${row.description}`}
                    />
                  </td>
                ) : null}

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}

                {/* Row actions kebab */}
                <td className="dp-td dp-td--actions">
                  <ServerRowActionsMenu
                    server={row}
                    deleteRights={deleteRights}
                    onDeleteDevice={onDeleteDevice}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * ServerRowActionsMenu — kebab menu for per-row server actions.
 * Only supports Delete.
 */
const ServerRowActionsMenu = ({
  server,
  deleteRights,
  onDeleteDevice,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!deleteRights) {
    return null;
  }

  return (
    <div className="dp-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="dp-row-actions__trigger"
        aria-label={`Actions for ${server.description}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="dp-row-actions__menu">
          <button
            type="button"
            onClick={() => {
              onDeleteDevice(server);
              setOpen(false);
            }}
            className="dp-row-actions__item dp-row-actions__item--danger"
          >
            <svg
              className="dp-icon-md dp-row-actions__item-icon"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
            Delete Server
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ServersTable;
